import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackBarService } from 'app/core/snack-bar.service';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import CampaignHelpers from '../../campaign/campaign-helpers';
import { CampaignPreviewData } from '../../campaign/campaign-overview/campaign-preview/campaign-preview-models';
import {
  AbTestMetric,
  CampaignCategoryCountsResponse,
  CampaignSettings,
  CampaignTableSort,
  CampaignTag,
  CampaignTrigger,
  CampaignUrlSetting,
  FilterAllCampaignsResponse,
  FilterCampaignsResponse,
  FilterSettings,
  FilterSettingsWithSort,
  OnsiteCampaign,
  OnsiteCampaignType,
  ReferralCampaignGroup,
  ResponseListProperties,
  TargetDevice,
} from '../../campaign/campaign.model';
import { CampaignCreationSuccessDialogComponent } from '../../campaign/campaigns/campaign-creation-success-dialog/campaign-creation-success-dialog.component';
import { TemplateTrigger, WidgetTemplateTagType } from '../../campaign/templates/template.model';
import { Widget, WidgetCondition } from '../../campaign/widgets.model';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { Site } from '../../core/site-specific.base';
import { HttpRequestDecorator } from '../../core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from '../../core/triggerbee/triggerbee-api.enum';
import { WidgetService } from './widget.service';
import { CampaignPreviewComponent } from 'app/campaign/campaign-overview/campaign-preview/campaign-preview.component';
import { CampaignFilterService } from 'app/campaign/campaign-filter.service';
import { CookieService } from 'app/core/cookie.service';
import { IndeterminateProgressDialogComponent } from '../progress/indeterminate-progress-dialog/indeterminate-progress-dialog.component';
import { ApplicationStarterService } from 'app/core/application-starter.service';
import { SiteContextService } from '../../core/site-context.service';
import { BatchCreateCampaignConfigurationRequest } from 'app/campaign/batch-create-campaigns.model';
import { PatchRequest } from '../patch-request.model';
import { PublishReadinessResponse } from 'app/campaign/publish-readiness.model';
import { TargetSite } from 'app/core/target-site';

@Injectable({ providedIn: 'root' })
export class CampaignService {
  private readonly logger: Logger;
  public campaignProgressDialogBS = new BehaviorSubject<boolean>(null);

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
    private widgetService: WidgetService,
    private snackbarService: SnackBarService,
    private readonly dialog: MatDialog,
    private readonly cookieService: CookieService,
    private readonly siteContextService: SiteContextService,
  ) {
    this.logger = loggerFactory.get('CampaignService');
  }

  private triggerEvent(campaignData$: Observable<OnsiteCampaign>, eventName: string) {
    campaignData$
      .pipe(
        switchMap((campaign: OnsiteCampaign) => {
          ApplicationStarterService.triggerEvent(eventName, {
            id: campaign?.id,
            siteId: campaign?.siteId ?? this.siteContextService.getCurrentSiteId(),
            type: campaign?.type,
          });
          return of();
        }),
      )
      .subscribe();
  }

  public getAllAccounts(): Observable<Array<Site>> {
    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/admin/accounts');
  }

  public getAllCampaignsOnAllAccount(): Observable<Array<OnsiteCampaign>> {
    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/admin/onsite-campaigns`);
  }

  public filterCampaignsOnAllAccounts(
    filter: FilterSettingsWithSort,
    skip: number = 0,
    take: number = 50,
  ): Observable<FilterAllCampaignsResponse> {
    const responseFilter = CampaignFilterService.cleanUpFilterWithSort(filter);
    responseFilter.skip = skip;
    responseFilter.take = take;
    return this.http.post(`${this.getAdminCampaignGatewayResource()}/filter`, responseFilter);
  }

  public getAllCampaigns(withRelations: boolean = false): Observable<Array<OnsiteCampaign>> {
    return this.http.get(this.getCampaignGatewayResource(withRelations ? '/with-relations' : ''));
  }

  public getFilteredCampaigns(filter: FilterSettings): Observable<Array<OnsiteCampaign>> {
    const responseFilter = CampaignFilterService.cleanUpFilter(filter);
    return this.http.post(this.getCampaignGatewayResource('/filter'), responseFilter);
  }

  public getFilteredCampaignsOnAccounts(filter: FilterSettings): Observable<Array<OnsiteCampaign>> {
    const responseFilter = CampaignFilterService.cleanUpFilter(filter);
    return this.http.post(this.getCampaignGatewayResource('/filter-on-accounts'), responseFilter);
  }

  // Omitting take returns every match, which the surfaces that aggregate across all campaigns rely on.
  public getFilteredCampaignsOnAccountsV2(
    filter: FilterSettings,
    skip?: number,
    take?: number,
    tableSort?: CampaignTableSort,
  ): Observable<FilterCampaignsResponse> {
    const responseFilter: FilterSettings = CampaignFilterService.cleanUpFilter(filter);
    responseFilter.skip = skip;
    responseFilter.take = take;
    return this.http.post(
      this.getCampaignGatewayResource('/v2/filter-on-accounts'),
      tableSort ? { ...responseFilter, tableSort } : responseFilter,
    );
  }

  public getCategoryCountsOnAccounts(
    filter: FilterSettings,
    categories: Record<string, string[]>,
  ): Observable<CampaignCategoryCountsResponse> {
    return this.http.post(this.getCampaignGatewayResource('/v2/category-counts'), {
      filter: CampaignFilterService.cleanUpFilter(filter),
      categories,
    });
  }

  public getCampaignsCount(type?: OnsiteCampaignType): Observable<number> {
    return this.http.post(this.getCampaignGatewayResource('/count'), {
      type: type,
    });
  }

  public getAllCampaignTags(): Observable<Array<CampaignTag>> {
    return this.http.get(this.getCampaignTagResource());
  }

  public getALlCampaignTagsWithCount(): Observable<Array<CampaignTag>> {
    return this.http.get(this.getCampaignTagsWithCountResource());
  }

  /**
   * @deprecated Handle this logic inside each own use-case instead
   */
  public getPatchOperations(campaign: OnsiteCampaign): PatchRequest[] {
    const patchOperations = [];

    if (campaign.name !== undefined) {
      patchOperations.push({
        value: campaign.name,
        path: '/Name',
        op: 'replace',
      });
    }

    if (campaign.description !== undefined) {
      patchOperations.push({
        value: campaign.description,
        path: '/Description',
        op: 'replace',
      });
    }

    if (campaign.scheduling !== undefined) {
      patchOperations.push({
        value: campaign.scheduling,
        path: '/Scheduling',
        op: 'replace',
      });
    }

    if (campaign.testMetric !== undefined) {
      patchOperations.push({
        value: campaign.testMetric,
        path: '/TestMetric',
        op: 'replace',
      });
    }
    return patchOperations;
  }

  public updateCampaignWithPatch(
    campaign: OnsiteCampaign,
    patchOperations: PatchRequest[],
  ): Observable<OnsiteCampaign> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaigns/${campaign.id}`;
    if (patchOperations.length === 0) {
      this.logger.warn(`Campaign PATCH aborted, no operations provided`);
      return of(campaign);
    }
    return this.http.patch(url, patchOperations);
  }

  public updateWidgetConditionOnAbTest(abTestId: number, conditionId: number, condition: WidgetCondition) {
    condition = CampaignHelpers.emptyAllBackendTypeProperties(condition) as WidgetCondition;
    return this.http.put(this.getAbTestGatewayResource() + '/' + abTestId + '/conditions/' + conditionId, condition);
  }

  public createWidgetConditionOnAbTest(abTestId: number, condition: WidgetCondition) {
    return this.http.post(this.getAbTestGatewayResource() + '/' + abTestId + '/conditions', condition);
  }

  public removeWidgetConditionOnAbTest(abTestId: number, conditionId: number) {
    return this.http.delete(this.getAbTestGatewayResource() + '/' + abTestId + '/conditions/' + conditionId);
  }

  public publishCampaign(campaignId: number) {
    const url = this.getCampaignGatewayResource(`/${campaignId}/publish`);
    const response = this.http.post(url, {}).pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignPublished');
    return response;
  }

  public getPublishReadiness(campaignId: number): Observable<PublishReadinessResponse> {
    return this.http.get(this.getCampaignGatewayResource(`/${campaignId}/publish-readiness`));
  }

  public publishCampaignV2(campaignId: number, acknowledgeWarnings: boolean) {
    const url = this.getCampaignV2GatewayResource(`/${campaignId}/publish?acknowledgeWarnings=${acknowledgeWarnings}`);
    const response = this.http.post(url, {}).pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignPublished');
    return response;
  }

  public getReferralCampaignPair(group: ReferralCampaignGroup): Observable<ReferralCampaignGroup> {
    const loadHalf = (campaignId: number): Observable<OnsiteCampaign> =>
      forkJoin([this.getCampaignById(campaignId), this.widgetService.getWidgetsByCampaignId(campaignId)]).pipe(
        map(([campaign, widgets]) => ({ ...campaign, widgets })),
      );
    return forkJoin({
      referringCampaign: loadHalf(group.referringCampaignId),
      friendCampaign: group.friendCampaignId ? loadHalf(group.friendCampaignId) : of(undefined),
    }).pipe(map((halves) => ({ ...group, ...halves })));
  }

  // Unenforced route: the pair is validated by the browser-side checks, not by server readiness.
  public publishReferralCampaignPair(pair: ReferralCampaignGroup): Observable<OnsiteCampaign[]> {
    const halves = [pair.referringCampaign, pair.friendCampaign].filter((half) => !!half);
    return forkJoin(halves.map((half) => this.publishCampaign(half.id)));
  }

  public unpublishReferralCampaignPair(group: ReferralCampaignGroup): Observable<OnsiteCampaign[]> {
    const halfIds = [group.referringCampaignId, group.friendCampaignId].filter((id) => !!id);
    return forkJoin(halfIds.map((id) => this.unpublishCampaign(id)));
  }

  public unpublishCampaign(campaignId: number) {
    const url = this.getCampaignGatewayResource(`/${campaignId}/unpublish`);
    const response = this.http.post(url, {}).pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignUnpublished');
    return response;
  }

  public deleteCampaign(campaignId: number): Observable<any> {
    const url = this.getCampaignGatewayResource(`/${campaignId}`);
    const response = this.http.delete(url, null).pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignDeleted');
    return response;
  }

  public getCampaignById(campaignId: number): Observable<OnsiteCampaign> {
    return this.http.get(this.getCampaignGatewayResource(`/${campaignId}`));
  }

  public copyAbTestToAccount(
    siteId: number,
    campaignId: number,
    overwriteExisting: boolean,
    publishAfterCopying: boolean,
    shouldStopOriginalCampaign: boolean,
    responseListProperties?: ResponseListProperties,
    translateIntoAccountLanguage?: boolean,
    translationTargetLanguage?: string,
  ): Observable<OnsiteCampaign> {
    return this.http.post(this.getAbTestGatewayInternalResource(`/${campaignId}/copy-to`), {
      siteId: siteId,
      overwriteExisting: overwriteExisting,
      publishAfterCopying: publishAfterCopying,
      shouldStopOriginalCampaign: shouldStopOriginalCampaign,
      responseListProperties,
      translateIntoAccountLanguage: translateIntoAccountLanguage,
      translationTargetLanguage: translationTargetLanguage,
    });
  }

  public updateAbTestCampaign(abTest: OnsiteCampaign) {
    abTest = CampaignHelpers.emptyAllBackendTypeProperties(abTest) as OnsiteCampaign;
    const url = this.getAbTestGatewayInternalResource(`/${abTest.id}`);
    return this.http.put(url, abTest);
  }

  public createCampaignTag(tag: CampaignTag): Observable<CampaignTag> {
    return this.http.post(this.getCampaignTagResource(), tag);
  }

  public updateCampaignTag(tag: CampaignTag): Observable<CampaignTag> {
    tag = CampaignHelpers.emptyAllBackendTypeProperties(tag) as CampaignTag;
    return this.http.put(this.getCampaignTagResource(`/${tag.id}`), tag);
  }

  public deleteCampaignTag(tagId: number): Observable<CampaignTag> {
    return this.http.delete(this.getCampaignTagResource(`/${tagId}`));
  }

  public addTagToOnsiteCampaign(campaignId: number, tagId: number): Observable<OnsiteCampaign> {
    return this.http.post(this.getCampaignGatewayResource(`/${campaignId}/tags`), { tagId: tagId });
  }

  public removeTagFromOnsiteCampaign(campaignId: number, tagId: number): Observable<OnsiteCampaign> {
    return this.http.delete(this.getCampaignGatewayResource(`/${campaignId}/tags/${tagId}`));
  }

  public getCampaignUrlSettings(campaignId: number): Observable<Array<CampaignUrlSetting>> {
    return this.http.get(this.getCampaignResource(`/${campaignId}/url-settings`));
  }

  public createCampaignUrlSetting(
    campaignId: number,
    campaignUrlSetting: CampaignUrlSetting,
  ): Observable<CampaignUrlSetting> {
    return this.http.post(this.getCampaignResource(`/${campaignId}/url-settings`), campaignUrlSetting);
  }

  public updateCampaignType(campaignId: number, type: OnsiteCampaignType): Observable<OnsiteCampaign> {
    return this.http.put(this.getCampaignGatewayResource(`/${campaignId}/change-type`), {
      type: type,
    });
  }

  public updateCampaignUrlSetting(
    campaignId: number,
    campaignUrlSetting: CampaignUrlSetting,
  ): Observable<CampaignUrlSetting> {
    campaignUrlSetting = CampaignHelpers.emptyAllBackendTypeProperties(campaignUrlSetting) as CampaignUrlSetting;
    return this.http.put(
      this.getCampaignResource(`/${campaignId}/url-settings/${campaignUrlSetting.id}`),
      campaignUrlSetting,
    );
  }

  public removeCampaignUrlSetting(campaignId: number, campaignUrlSettingId: number): Observable<string> {
    return this.http.delete(this.getCampaignResource(`/${campaignId}/url-settings/${campaignUrlSettingId}`));
  }

  public getCampaignTriggers(campaignId: number): Observable<Array<CampaignTrigger>> {
    return this.http.get(this.getCampaignResource(`/${campaignId}/triggers`));
  }

  public createCampaignTrigger(campaignId: number, campaignTrigger: CampaignTrigger): Observable<CampaignTrigger> {
    return this.http.post(this.getCampaignResource(`/${campaignId}/triggers`), campaignTrigger);
  }

  public updateCampaignTrigger(campaignId: number, campaignTrigger: CampaignTrigger): Observable<CampaignTrigger> {
    campaignTrigger = CampaignHelpers.emptyAllBackendTypeProperties(campaignTrigger) as CampaignTrigger;
    return this.http.put(this.getCampaignResource(`/${campaignId}/triggers/${campaignTrigger.id}`), campaignTrigger);
  }

  public removeCampaignTrigger(campaignId: number, campaignTriggerId: number): Observable<string> {
    return this.http.delete(this.getCampaignResource(`/${campaignId}/triggers/${campaignTriggerId}`));
  }

  public createOnsiteCampaignFromTemplate(
    widgetTemplateId: number,
    campaignType: OnsiteCampaignType,
    defaultFontFamily: string,
    typeTemplate: string,
    purposeId?: number,
    isPoweredByDisabled = false,
    isAttributionActivated = false,
    targetDevice?: TargetDevice,
  ): Observable<OnsiteCampaign> {
    const urlPath = campaignType === OnsiteCampaignType.ABTesting ? '/onsite-campaign-ab-testing' : '/onsite-campaigns';
    const createCampaignData$ = this.http
      .post(this.http.getBaseUrl(TriggerbeeApi.Gateway) + urlPath, {
        templateId: widgetTemplateId,
        tagTypeId: purposeId,
        isPoweredByDisabled: isPoweredByDisabled,
        defaultFontFamily: defaultFontFamily,
        testMetric: campaignType === OnsiteCampaignType.ABTesting ? AbTestMetric.Conversion : undefined,
        type: typeTemplate,
        isAttributionActivated: isAttributionActivated,
        targetDevice: targetDevice,
      })
      .pipe(shareReplay(1));
    this.triggerEvent(createCampaignData$, 'onTriggerbeeClientCampaignCreated');
    return createCampaignData$;
  }

  public createBatch(configurations: BatchCreateCampaignConfigurationRequest[]): Observable<OnsiteCampaign> {
    const createCampaignData$ = this.http
      .post(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/onsite-campaigns/batch/queue', {
        configurations: configurations,
      })
      .pipe(shareReplay(1));
    ApplicationStarterService.triggerEvent('onTriggerbeeClientCampaignCreated', {}); // Empty object to avoid errors. We cannot pass multiple when using same event name
    return createCampaignData$;
  }

  public createReferralCampaign(referringCampaignId: number, name: string): Observable<OnsiteCampaign> {
    const response = this.http
      .post(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/referral-campaigns', {
        referringCampaignId,
        name,
      })
      .pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignCreated');
    return response;
  }

  public getReferralCampaigns(): Observable<Array<OnsiteCampaign>> {
    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/referral-campaigns');
  }

  public updateReferralCampaign(campaignReferral): Observable<OnsiteCampaign> {
    const updatingReferralCampain = {
      name: campaignReferral.name,
      referringCampaignId: campaignReferral.referringCampaignId || campaignReferral.referringCampaign.id,
      friendCampaignId: campaignReferral.friendCampaign?.id,
    };
    return this.http.put(
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/referral-campaigns/' + campaignReferral.id,
      updatingReferralCampain,
    );
  }

  public deleteReferralCampaign(id: number): Observable<any> {
    const response = this.http
      .delete(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/referral-campaigns/' + id)
      .pipe(shareReplay(1));
    this.triggerEvent(response, 'onTriggerbeeClientCampaignDeleted');
    return response;
  }

  public copyReferralCampaign(
    siteIds: Array<number>,
    id: number,
    overwriteExisting: boolean,
    publishAfterCopying: boolean,
    responseListProperties?: ResponseListProperties,
    translateIntoAccountLanguage?: boolean,
  ): Observable<any> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/referral-campaigns/${id}/copy-to`;
    return this.http.post(url, {
      siteIds: siteIds,
      overwriteExisting: overwriteExisting,
      publishAfterCopying: publishAfterCopying,
      responseListProperties,
      translateIntoAccountLanguage: translateIntoAccountLanguage,
    });
  }

  public copyCampaignToAccount(
    siteId: number,
    campaignId: number,
    campaignType: OnsiteCampaignType,
    overwriteExisting: boolean,
    publishAfterCopying: boolean,
    responseListProperties?: ResponseListProperties,
    translateIntoAccountLanguage?: boolean,
    translationTargetLanguage?: string,
  ): Observable<any> {
    const url =
      campaignType !== OnsiteCampaignType.ABTesting
        ? this.getCampaignGatewayResource(`/${campaignId}/copy-to`)
        : this.getAbTestCampaignResource(`/${campaignId}/copy-to`);
    return this.http.post(url, {
      siteId: siteId,
      overwriteExisting: overwriteExisting,
      publishAfterCopying: publishAfterCopying,
      responseListProperties,
      translateIntoAccountLanguage: translateIntoAccountLanguage,
      translationTargetLanguage: translationTargetLanguage,
    });
  }

  public copyCampaignToTemplate(campaignId: number): Observable<any> {
    const url = this.getAdminCampaignGatewayResource(`/${campaignId}/copy-to/templates`);
    return this.http.post(url, null);
  }

  public async copyCampaignToAccounts(
    siteIds: Array<number>,
    campaignId: number,
    campaignType: OnsiteCampaignType,
    overwriteExisting: boolean,
    publishAfterCopying: boolean,
    translateIntoAccountLanguage?: boolean,
  ): Promise<number[]> {
    const isAbTest = campaignType === OnsiteCampaignType.ABTesting;
    if (isAbTest) {
      const results: number[] = [];
      for (const siteId of siteIds) {
        const result = await this.copyCampaignToAccount(
          siteId,
          campaignId,
          campaignType,
          overwriteExisting,
          publishAfterCopying,
          undefined,
          translateIntoAccountLanguage,
        )
          .toPromise()
          .then(() => 200)
          .catch((x) => x.status);
        results.push(result);
      }
      return results;
    } else {
      const url = this.getCampaignGatewayResource(`/${campaignId}/copy-to/batch`);
      const response = <number[]>await this.http
        .post(url, {
          siteIds: siteIds,
          overwriteExisting: overwriteExisting,
          publishAfterCopying: publishAfterCopying,
          translateIntoAccountLanguage: translateIntoAccountLanguage,
        })
        .toPromise();
      return response;
    }
  }

  private getCampaignResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaigns` + (subResource || '');
  }

  private getAbTestCampaignResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-ab-testing` + (subResource || '');
  }

  private getAbTestGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-ab-testing` + (subResource || '');
  }

  private getAbTestGatewayInternalResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-ab-testing` + (subResource || '');
  }

  private getCampaignGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaigns` + (subResource || '');
  }

  // Rooted at /v2, unlike the /v2/ sub-resources elsewhere in this file.
  private getCampaignV2GatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/v2/onsite-campaigns` + (subResource || '');
  }

  private getAdminCampaignGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/admin/onsite-campaigns` + (subResource || '');
  }

  private getCampaignTagResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-tags` + (subResource || '');
  }

  private getCampaignTagsWithCountResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-tags` + (subResource || '');
  }

  // TODO: Change resource method when template triggers and conditions will ready on backend
  public getTemplateTriggers(templateId: number): Observable<Array<TemplateTrigger>> {
    return this.http.get(this.getCampaignResource(`/${templateId}/triggers`));
  }

  public createTemplateTrigger(templateId: number, templateTrigger: TemplateTrigger): Observable<TemplateTrigger> {
    return this.http.post(this.getCampaignResource(`/${templateId}/triggers`), templateTrigger);
  }

  public updateTemplateTrigger(templateId: number, templateTrigger: TemplateTrigger): Observable<TemplateTrigger> {
    templateTrigger = CampaignHelpers.emptyAllBackendTypeProperties(templateTrigger) as TemplateTrigger;
    return this.http.put(this.getCampaignResource(`/${templateId}/triggers/${templateTrigger.id}`), templateTrigger);
  }

  public removeTemplateTrigger(templateId: number, templateTriggerId: number): Observable<string> {
    return this.http.delete(this.getCampaignResource(`/${templateId}/triggers/${templateTriggerId}`));
  }

  // Repetition
  public getRepetitions(campaignId: number): Observable<any> {
    const url = this.getCampaignGatewayResource(`/${campaignId}/repetition-settings`);
    return this.http.get(url);
  }

  public createRepetition(campaignId: number, data: { type: string; value?: number; option: string }): Observable<any> {
    const url = this.getCampaignGatewayResource(`/${campaignId}/repetition-settings`);
    return this.http.post(url, data);
  }

  public updateRepetition(
    campaignId: number,
    data: { type: string; value?: number; option: string },
    id,
  ): Observable<any> {
    const url = this.getCampaignGatewayResource(`/${campaignId}/repetition-settings/${id}`);
    data = CampaignHelpers.emptyAllBackendTypeProperties(data);
    return this.http.put(url, data);
  }

  // Shared
  public isPublishPossible(campaignId: number): Observable<boolean> {
    return this.getCampaignTriggers(campaignId).pipe(
      map((triggers: CampaignTrigger[]) => {
        const hasTriggers = triggers && triggers.length > 0;
        return hasTriggers;
      }),
    );
  }

  public createAbTestOnCampaign(campaignId: number): Observable<OnsiteCampaign> {
    const data = {
      campaignId: campaignId,
      testMetric: AbTestMetric.Conversion,
    };
    return this.http.post(this.getAbTestGatewayInternalResource(), data);
  }

  public createCampaignFromAbTest(campaignId: number, winningWidgetId: number): Observable<OnsiteCampaign> {
    const data = {
      abTestingCampaignId: campaignId,
      winningWidgetId: winningWidgetId,
      loosingWidgetStatus: 'Disabled',
    };
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaigns`;
    return this.http.post(url, data);
  }

  public previewWidgetOnCampaign(
    campaign: OnsiteCampaign,
    accountDomainName: string,
    overrideDomainName: string,
    widget: Widget,
    accountHasVisits: boolean,
  ): void {
    new Promise((resolve, reject) => {
      this.getCampaignUrlSettings(campaign.id).subscribe((urlSettings: Array<CampaignUrlSetting>) => {
        if (urlSettings) {
          resolve(urlSettings);
        } else {
          reject();
        }
      });
    })
      .then((urlSettings: CampaignUrlSetting[]) => {
        this.campaignProgressDialogBS.next(true);
        let widgetId = undefined;
        this.widgetService
          .getWidgetsByCampaignId(campaign.id)
          .pipe(
            switchMap(() => {
              widgetId = widget.id;
              return this.widgetService.prepareWidgetPreview(widgetId);
            }),
            catchError((error) => {
              this.campaignProgressDialogBS.next(false);
              this.snackbarService.error('campaign.preview-error');
              this.logger.error('Widget could not be previewed: ' + JSON.stringify(error));
              widgetId = undefined;
              return of(undefined);
            }),
          )
          .subscribe(
            () => {
              this.campaignProgressDialogBS.next(false);
              if (widgetId) {
                this.openCampaignPreview(
                  campaign,
                  accountDomainName,
                  overrideDomainName,
                  widgetId,
                  urlSettings,
                  accountHasVisits,
                );
              }
            },
            (error) => {
              this.campaignProgressDialogBS.next(false);
              this.snackbarService.error('campaign.preview-error');
              this.logger.error('Widget could not be previewed: ' + JSON.stringify(error));
            },
          );
      })
      .catch((err) => {
        this.logger.error('Widget could not be previewed: ' + JSON.stringify(err));
      });
  }

  // Quick preview renders in the preview flyout rather than on the customer's own site: leaving
  // campaignSiteId out of the context is what routes previewWidget to the flyout. The progress
  // dialog is owned here, so the flow still shows and finishes when the caller closes itself
  // (CampaignCardOverlay does that the moment the button is pressed).
  //
  // siteId names the campaign's own account when it is not the active one: the prepare call is
  // scoped to it and previewSiteId carries it into the preview URL, so no account switch is needed.
  public quickPreviewWidget(campaignId: number, widgetId: number, siteId?: number): void {
    const progressDialog = this.showCampaignProgressDialog();
    const prepare = siteId
      ? TargetSite.withSiteSync(siteId, () => this.widgetService.prepareWidgetPreview(widgetId))
      : this.widgetService.prepareWidgetPreview(widgetId);
    prepare.subscribe({
      next: () => {
        this.closeCampaignProgressDialog(progressDialog);
        this.widgetService.previewWidget(
          undefined,
          widgetId,
          undefined,
          undefined,
          { campaignId, previewSiteId: siteId },
          undefined,
          true,
        );
      },
      error: (error) => {
        this.closeCampaignProgressDialog(progressDialog);
        this.snackbarService.error('campaign.preview-error');
        this.logger.error('Widget could not be quick previewed: ' + JSON.stringify(error));
      },
    });
  }

  public openCampaignPreview(
    campaign: OnsiteCampaign,
    accountDomainName: string,
    overrideDomainName: string,
    widgetId: number,
    urlSettings: Array<CampaignUrlSetting>,
    accountHasVisits: boolean,
    productToursPreviewStateId: number = undefined,
    token: string = undefined,
  ): Window | null {
    let pagePath: string;
    const protocol =
      overrideDomainName?.match('(http|https|ftp|ftps)://') || accountDomainName?.match('(http|https|ftp|ftps)://');
    if (campaign?.placeholder?.previewUrl) {
      const placeholderPreviewUrl = campaign.placeholder.previewUrl;
      const placeholderExceptHttp = placeholderPreviewUrl.slice(
        placeholderPreviewUrl.match('(http|https|ftp|ftps)://')[0].length,
      );
      const idxSlash = placeholderExceptHttp.indexOf('/');
      const isSlashLast = placeholderExceptHttp.length !== idxSlash + 1;
      if (idxSlash !== -1 && isSlashLast) {
        const placeholderPath = placeholderExceptHttp.slice(placeholderExceptHttp.indexOf('/'));
        pagePath = placeholderPath;
        overrideDomainName = placeholderPreviewUrl.slice(0, placeholderPreviewUrl.indexOf(placeholderPath));
      } else {
        overrideDomainName = !isSlashLast
          ? campaign.placeholder.previewUrl.slice(0, campaign.placeholder.previewUrl.length - 1)
          : campaign.placeholder.previewUrl;
        pagePath = CampaignHelpers.getCorrectPathFromCampaignSettingsForPreview(urlSettings);
      }
    } else {
      pagePath = CampaignHelpers.getCorrectPathFromCampaignSettingsForPreview(urlSettings);
    }
    pagePath = CampaignHelpers.trimWildcardsForPreview(pagePath);
    const resultCamparePath = this.comparePreviewPath(pagePath, overrideDomainName);
    if (resultCamparePath) {
      pagePath = '/' + resultCamparePath.path;
      overrideDomainName = resultCamparePath.domain;
    }

    return this.widgetService.previewWidget(
      accountDomainName,
      widgetId,
      overrideDomainName,
      pagePath,
      { campaignId: campaign.id, campaignSiteId: campaign.siteId },
      protocol,
      accountHasVisits,
      productToursPreviewStateId,
      token,
    );
  }

  public showCampaignCreationSuccessDialog(
    widgets: Widget[],
    campaign: OnsiteCampaign,
    campaignPreviewData: CampaignPreviewData,
  ): MatDialogRef<CampaignCreationSuccessDialogComponent> | null {
    let hidePublishSuccessDialog = this.cookieService.getTypedValue('hidePublishSuccessDialog');
    if (!hidePublishSuccessDialog) {
      const creationSuccessDialogRef = this.dialog.open(CampaignCreationSuccessDialogComponent, {
        panelClass: 'campaign-creation-success-dialog-container',
        disableClose: false,
        autoFocus: false,
        data: {
          widgets: widgets,
          campaign: campaign,
          campaignPreviewData: campaignPreviewData,
        },
      });
      return creationSuccessDialogRef;
    } else {
      //show snackComponent instead of the dialog
      this.snackbarService.success(
        'widgets.campaigns.publish-success.campaign-successfully-published',
        null,
        10000,
        null,
        {
          component: CampaignPreviewComponent,
          properties: {
            data: campaignPreviewData,
            translationKey: 'generic.view',
            accountHasVisits: true,
            classComponent: 'preview-snack-bar',
          },
        },
      );
    }
  }

  public comparePreviewPath(pagePath, overrideDomainName) {
    if (!overrideDomainName) {
      return;
    }
    const arrayPathCampaign = pagePath.split('/').filter((word) => {
      return word.trim() !== '';
    });
    const arrayPathDomain = overrideDomainName
      .replace(/^https?:\/\//, '')
      .split('/')
      .filter((word) => {
        return word.trim() !== '';
      });
    const domain = arrayPathDomain[0];
    arrayPathDomain.shift();
    //if both paths completely coincide
    if (
      arrayPathCampaign.length === arrayPathDomain.length &&
      arrayPathCampaign.every((value, index) => value === arrayPathDomain[index])
    ) {
      return { domain, path: arrayPathCampaign.join('/') };
    }
    const resultArray = [];
    const minLength = Math.min(arrayPathCampaign.length, arrayPathDomain.length);
    //if paths does not coincide we need to compare them
    for (let i = 0; i < minLength; i++) {
      if (arrayPathCampaign[i] !== arrayPathDomain[arrayPathDomain.length - 1 - i]) {
        resultArray.push(arrayPathCampaign[i]);
        resultArray.unshift(arrayPathDomain[arrayPathDomain.length - 1 - i]);
      } else {
        if (i === 0) {
          resultArray.push(arrayPathCampaign[i]);
        } else if (arrayPathCampaign[i] !== 0) {
          resultArray.unshift(arrayPathDomain[arrayPathDomain.length - 1 - i]);
          resultArray.push(arrayPathCampaign[i]);
        }
      }
    }
    //if paths have different length
    if (arrayPathDomain.length > minLength) {
      resultArray.unshift(...arrayPathDomain.slice(0, arrayPathDomain.length - minLength));
    }
    if (arrayPathCampaign.length > minLength) {
      resultArray.push(...arrayPathCampaign.slice(-(arrayPathCampaign.length - minLength)));
    }
    return { domain, path: resultArray.join('/') };
  }

  public getCampaignCustomerSettings(customerId: string): Observable<CampaignSettings> {
    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/campaigns/settings/${customerId}`);
  }

  public updateCampaignCustomerSettings(propsCampaignToChange: any, customerId): Observable<CampaignSettings> {
    return this.http.patch(
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/campaigns/settings/${customerId}`,
      propsCampaignToChange,
    );
  }

  public createCampaignCustomerSettings(allowMultipleOverlays: boolean, customerId): Observable<CampaignSettings> {
    const body = {
      allowMultipleOverlays,
      id: customerId,
    };
    return this.http.post(this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/campaigns/settings`, body);
  }

  public showCampaignProgressDialog(): MatDialogRef<IndeterminateProgressDialogComponent> {
    return this.dialog.open(IndeterminateProgressDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
  }

  public closeCampaignProgressDialog(dialogRef: MatDialogRef<IndeterminateProgressDialogComponent>) {
    if (dialogRef) {
      dialogRef.close();
    }
  }

  public addTagToCampaignFromNewTemplatesCategories(campaignTypes, widgetTemplate, campaign): void {
    let campaignTypesByTag = campaignTypes.map((tag) => tag.toLowerCase());
    //get tag UI-Tour/Games/Nudge/Quiz
    let tagOnCurrentTemplate = widgetTemplate.tagsDetails?.find(
      (tag) => campaignTypesByTag.includes(tag.name.toLowerCase()) && tag.type === WidgetTemplateTagType.Regular,
    );
    if (!tagOnCurrentTemplate) return;
    //get all campaign tags
    this.getAllCampaignTags().subscribe(
      (campaignTags: CampaignTag[]) => {
        let allCampaignTags = campaignTags;
        //looking for the same tag in template/campaign
        let tagOnCampaign = allCampaignTags.find(
          (tag) => tag.name.toLowerCase() === tagOnCurrentTemplate?.name.toLowerCase(),
        );
        //if tag does not exist in campaign we need to create  this tag
        if (!tagOnCampaign) {
          const newTag = <CampaignTag>{};
          newTag.name = tagOnCurrentTemplate.name;
          newTag.color = '#0093B2';
          this.createCampaignTag(newTag)
            .pipe(
              switchMap((createdTag: CampaignTag) => {
                //then we need to add this tag to campaign
                return this.addTagToOnsiteCampaign(campaign.id, createdTag.id);
              }),
              catchError((error) => {
                throw error;
              }),
            )
            .subscribe(() => {});
          //if tag exist in campaign we just add this tag to campaign
        } else if (tagOnCampaign) {
          this.addTagToOnsiteCampaign(campaign.id, tagOnCampaign.id).subscribe();
        }
      },
      (error) => {
        this.logger.error(
          'Error that should break the page, campaign tags could not be retrieved: ' + JSON.stringify(error),
        );
      },
    );
  }
}
